import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Eye, EyeOff, Plus, ShieldCheck, ShieldOff, Trash2, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import {
  AdminUserRecord,
  CreateAdminUserPayload,
  ManageableAdminRole,
  apiService,
} from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { canResetTwoFactor } from '../../utils/twoFactor';

interface AdminUsersPageProps {
  /** ID of the currently logged-in admin — disables self-destructive actions in the UI. */
  currentUserId: string;
}

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Portal Admin',
  support: 'Support (Legacy)',
  sales: 'Sales (Legacy)',
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE');
};

const isManageableRole = (role: string): role is ManageableAdminRole =>
  role === 'super_admin' || role === 'admin';

export function AdminUsersPage({ currentUserId }: AdminUsersPageProps) {
  const [admins, setAdmins] = useState<AdminUserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUserRecord | null>(null);
  const [resetting, setResetting] = useState(false);

  const [form, setForm] = useState<CreateAdminUserPayload>({
    email: '',
    name: '',
    password: '',
    role: 'admin',
  });
  const [showPassword, setShowPassword] = useState(false);

  const loadAdmins = async (): Promise<void> => {
    setLoading(true);
    try {
      const list = await apiService.getAdminUsers();
      setAdmins(list);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Admin-Liste konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAdmins();
  }, []);

  const resetForm = (): void => {
    setForm({ email: '', name: '', password: '', role: 'admin' });
    setShowPassword(false);
  };

  const handleCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim() || form.password.length < 8) {
      toast.error('Bitte E-Mail, Namen und ein Passwort mit mindestens 8 Zeichen angeben.');
      return;
    }
    setCreating(true);
    try {
      const created = await apiService.createAdminUser({
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
      });
      setAdmins((prev) => [created, ...prev]);
      toast.success(`Admin ${created.email} angelegt.`);
      setCreateOpen(false);
      resetForm();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Anlegen fehlgeschlagen.'));
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (admin: AdminUserRecord, role: ManageableAdminRole): Promise<void> => {
    if (admin.role === role) return;
    try {
      const updated = await apiService.updateAdminUser(admin.id, { role });
      setAdmins((prev) => prev.map((a) => (a.id === admin.id ? updated : a)));
      toast.success(`Rolle von ${admin.email} aktualisiert.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Rolle konnte nicht geändert werden.'));
    }
  };

  const handleActiveToggle = async (admin: AdminUserRecord, isActive: boolean): Promise<void> => {
    try {
      const updated = await apiService.updateAdminUser(admin.id, { isActive });
      setAdmins((prev) => prev.map((a) => (a.id === admin.id ? updated : a)));
      toast.success(isActive ? `${admin.email} aktiviert.` : `${admin.email} deaktiviert.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Status konnte nicht geändert werden.'));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.deleteAdminUser(deleteTarget.id);
      setAdmins((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      toast.success(`${deleteTarget.email} gelöscht.`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Löschen fehlgeschlagen.'));
    } finally {
      setDeleting(false);
    }
  };

  /** Setzt die 2FA des gewählten Nutzers zurück, meldet das Ergebnis per Toast und lädt die Liste neu. */
  const handleResetTwoFactor = async (): Promise<void> => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const result = await apiService.resetUserTwoFactor(resetTarget.id);
      if (result.notificationSent) {
        toast.success(`2FA von ${resetTarget.email} zurückgesetzt.`);
      } else {
        toast.warning(
          `2FA von ${resetTarget.email} zurückgesetzt, aber die Benachrichtigungs-E-Mail konnte nicht versendet werden.`,
        );
      }
      setResetTarget(null);
      await loadAdmins();
    } catch (err) {
      toast.error(extractErrorMessage(err, '2FA konnte nicht zurückgesetzt werden.'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <UserCog className="w-6 h-6 text-blue-600" />
            Admin Users
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Verwalte Callbert-interne Admin-Accounts. Nur Super-Admins haben Zugriff auf diese Seite.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Neuen Admin anlegen
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alle Admin-Accounts</CardTitle>
          <CardDescription>
            Super Admin = voller Zugriff. Portal Admin = Übersicht, Kunden, Abrechnungen, Registrierungsanfragen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Admins…</div>
          ) : admins.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">Keine Admin-Accounts vorhanden.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Aktiv</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Letzter Login</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => {
                  const isSelf = admin.id === currentUserId;
                  const manageable = isManageableRole(admin.role);
                  return (
                    <TableRow key={admin.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {admin.name}
                          {isSelf && <Badge variant="secondary">Du</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">{admin.email}</TableCell>
                      <TableCell>
                        {manageable ? (
                          <Select
                            value={admin.role}
                            onValueChange={(value) =>
                              handleRoleChange(admin, value as ManageableAdminRole)
                            }
                            disabled={isSelf}
                          >
                            <SelectTrigger className="w-44">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                              <SelectItem value="admin">Portal Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline">{ROLE_LABEL[admin.role] ?? admin.role}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={admin.isActive}
                          onCheckedChange={(checked) => handleActiveToggle(admin, checked)}
                          disabled={isSelf}
                        />
                      </TableCell>
                      <TableCell>
                        {admin.twoFactorEnabled ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            an
                          </Badge>
                        ) : (
                          <Badge variant="outline">aus</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600">{formatDate(admin.lastLoginAt)}</TableCell>
                      <TableCell className="text-right">
                        {canResetTwoFactor(admin, currentUserId) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setResetTarget(admin)}
                            title="2FA zurücksetzen"
                            aria-label={`2FA von ${admin.email} zurücksetzen`}
                          >
                            <ShieldOff className="w-4 h-4 mr-1" />
                            2FA zurücksetzen
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(admin)}
                          disabled={isSelf}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Neuen Admin anlegen</DialogTitle>
              <DialogDescription>
                Lege einen Callbert-internen Admin-Account an. Das initiale Passwort kann der Empfänger nach dem
                ersten Login unter „Mein Profil" ändern.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">E-Mail</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-name">Name</Label>
                <Input
                  id="admin-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Initiales Passwort</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    minLength={8}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">Mindestens 8 Zeichen.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-role">Rolle</Label>
                <Select
                  value={form.role}
                  onValueChange={(value) => setForm((f) => ({ ...f, role: value as ManageableAdminRole }))}
                >
                  <SelectTrigger id="admin-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Portal Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Wird angelegt…' : 'Admin anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  <strong>{deleteTarget.email}</strong> wird unwiderruflich entfernt. Der Account verliert
                  sofort den Zugriff aufs Admin-Portal.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? 'Lösche…' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetTarget} onOpenChange={(open) => !open && !resetting && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>2FA zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              {resetTarget && (
                <>
                  Die Zwei-Faktor-Authentifizierung von <strong>{resetTarget.email}</strong> wird deaktiviert und
                  das bisherige Secret gelöscht. Der Nutzer erhält eine E-Mail und kann 2FA anschließend neu
                  einrichten. Der Reset wird protokolliert.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleResetTwoFactor();
              }}
              disabled={resetting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {resetting ? 'Setze zurück…' : '2FA zurücksetzen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
